import React, { useState, useEffect, useRef } from "react";
import {
  callSuccessToast,
  cleanObject,
  removeError,
} from "../../../utilities/utilities";
import { ErrorResponse } from "../../../Models/Error";
import {
  AddressBook,
  convertAddressBookToCreate,
  convertAddressBookToUpdate,
  normalize,
  validate,
} from "../../../Models/AddressBook";
import { AddressBookService as ShipmentAddressBookService } from "../../../services/shipment/addressBook.ts";
import { AddressBookService as RegistryAddressBookService } from "../../../services/registry/addressBook.ts";
import { AddressBookService as LogisticAddressBookService } from "../../../services/logistic/addressBook.ts";
import { createModalConfig, ModalConfig } from "../../../Models/ModalConfig";
import { useAppContext } from "../../../AppProvider";
import { useRegistryAddressBook } from "../../../hooks/useRegistryAddressBook";
import { AddressBookHandlerContext } from "./";
import { AddressBookHandlerProviderProps } from "./";

/* ===========================================================================
 * SERVICE REGISTRY
 * Maps provider service type to the correct API service implementation.
 * =========================================================================== */
const services = {
  logistic: { addressBookService: new LogisticAddressBookService() },
  registry: { addressBookService: new RegistryAddressBookService() },
  shipment: { addressBookService: new ShipmentAddressBookService() },
};

/* ===========================================================================
 * PROVIDER: AddressBookHandlerProvider
 * =========================================================================== */

/**
 * @component AddressBookHandlerProvider
 * * Centralizes the state management and business logic for CRUD operations
 * on AddressBook entities.
 * * Responsibilities:
 * - Loading an existing AddressBook (`getAddressBook`)
 * - Creating or updating an AddressBook (`saveAddressBook`)
 * - Specialized save logic for the registry service (`saveRegistryAddressBook`)
 * - Autosave mode for real-time updates
 * - Modal configuration handling
 * - Error forwarding and external error synchronization
 *
 * @param {AddressBookHandlerProviderProps} props - Provider configuration.
 * @returns {JSX.Element} A React Context Provider for AddressBook operations.
 */
const AddressBookHandlerProvider: React.FC<AddressBookHandlerProviderProps> = ({
  id,
  children,
  action,
  ownerId,
  autosave = false,
  preLoader = false,
  label,
  callback,
  service = "registry",
  defaultValue = null,
  registry = { saveEnabled: false, searchEnabled: false },
  inError = false,
  isBilling = false,
  fieldsToShow = { email: true, phone: true },
  searchInAddressBookParams,
  getErrors,
}) => {
  /* ---------------------------------------------------------------------------
   * HOOKS & SERVICES SETUP
   * ------------------------------------------------------------------------- */
  const addressBookService = services[service].addressBookService;
  const { dictionary, accessTokenPayload } = useAppContext();
  const { saveRegistryAddressBook: saveToRegistry } = useRegistryAddressBook();

  /* ---------------------------------------------------------------------------
   * STATE MANAGEMENT
   * ------------------------------------------------------------------------- */

  /** Local AddressBook state with default flags initialization. */
  const [data, setData] = useState<Partial<AddressBook>>({
    myAddress: false,
    gdo: false,
    ztl: false,
    priv: false,
    ownerId,
    ...defaultValue,
  });

  /** UI State: loading indicators and modal configs. */
  const [loader, setLoader] = useState<boolean>(preLoader);
  const [modalConfig, setModalConfig] =
    useState<ModalConfig>(createModalConfig());

  /** Error State: tracking current and previous validation/API errors. */
  const [error, setError] = useState<ErrorResponse<AddressBook> | null>(null);
  const prevError = useRef<ErrorResponse<AddressBook> | null>(null);

  /* ---------------------------------------------------------------------------
   * FUNCTION: getAddressBook
   * ------------------------------------------------------------------------- */

  /**
   * Fetches and loads a single AddressBook entity by ID.
   *
   * @async
   * @param {string} id - The AddressBook ID to retrieve.
   * @returns {Promise<void>}
   */
  const getAddressBook = async (id: string): Promise<void> => {
    setLoader(true);
    try {
      const res = await addressBookService.get(id);

      // Attach client-only fields without overwriting backend fields
      res.registryId = data.registryId;
      res.location.registryId = data.location?.registryId;

      if (action === "clone") {
        const addressBook: Partial<AddressBook> = { ...res };
        delete addressBook.id;
        setData(addressBook);
        return;
      }

      setData(res);
    } catch (err) {
      setError(err as ErrorResponse<AddressBook>);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * FUNCTION: saveRegistryAddressBook
   * ------------------------------------------------------------------------- */

  /**
   * Saves the AddressBook using the registry-specific logic.
   *
   * @async
   * @returns {Promise<void>}
   */
  const saveRegistryAddressBook = async (): Promise<void> => {
    setLoader(true);
    try {
      const updated = await saveToRegistry(data as AddressBook);
      if (updated) {
        setData(updated);
        callSuccessToast(dictionary.messages.save_successful);
      }
    } catch (err) {
      setError(err as ErrorResponse<AddressBook>);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * FUNCTION: saveAddressBook (Logic Switcher)
   * ------------------------------------------------------------------------- */

  /**
   * Validates and persists an AddressBook entity.
   * Coordinates normalization, cleaning, and chooses between Create or Edit.
   *
   * @param {AddressBook} addressBook - The AddressBook model being modified.
   * @param {keyof AddressBook | null} property - Field whose validation error should be cleared.
   * @param {boolean} [save=true] - Whether to perform backend persistence.
   * @returns {void}
   */
  const saveAddressBook = (
    addressBook: AddressBook,
    property: keyof AddressBook | null,
    save = true,
  ) => {
    // 1. Data Normalization
    const normalized = normalize(addressBook) as AddressBook;
    const cleaned = cleanObject(normalized) as AddressBook;

    // 2. Error Cleanup
    if (property === "registryId") {
      setError(null);
    } else if (property) {
      removeError(property, error, prevError, setError);
    }

    setData(cleaned);

    if (!save) return;

    if (!autosave) setLoader(true);

    // 3. Validation Logic
    const validation = validate(cleaned);
    if (!validation.isValid) {
      if (!autosave || cleaned.id) {
        setError({ response: { data: validation.errors } });
      }
      setLoader(false);
      return;
    }

    // 4. Persistence Branching
    cleaned.id ? edit(cleaned) : create(cleaned);
  };

  /* ---------------------------------------------------------------------------
   * FUNCTION: create (Internal persistence)
   * ------------------------------------------------------------------------- */

  /**
   * Performs the API call to create a new AddressBook.
   *
   * @async
   * @param {AddressBook} addressBook - Entity data to be created.
   * @returns {Promise<void>}
   */
  const create = async (addressBook: AddressBook): Promise<void> => {
    try {
      const payload = convertAddressBookToCreate(addressBook);
      const created = await addressBookService.create(payload);

      setData(created);
      callback?.(created);
    } catch (err) {
      setError(err as ErrorResponse<AddressBook>);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * FUNCTION: edit (Internal persistence)
   * ------------------------------------------------------------------------- */

  /**
   * Performs the API call to update an existing AddressBook.
   *
   * @async
   * @param {AddressBook} addressBook - Entity data to be updated.
   * @returns {Promise<void>}
   */
  const edit = async (addressBook: AddressBook): Promise<void> => {
    try {
      const payload = convertAddressBookToUpdate(addressBook);
      await addressBookService.edit(payload);

      setData(addressBook);
      callback?.(addressBook);
    } catch (err) {
      setError(err as ErrorResponse<AddressBook>);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * EFFECTS
   * ------------------------------------------------------------------------- */

  /** External Error Sync: Propagates local error state to parent callback. */
  useEffect(() => {
    if (getErrors) {
      getErrors(error);
    }
  }, [error]);

  /** Parent Error Sync: Triggers validation if parent flags the form as in-error. */
  useEffect(() => {
    if (inError) {
      const validation = validate(data as AddressBook);
      setError({ response: { data: validation.errors } });
    }
  }, [inError]);

  /** Data Initialization: Fetches record if an ID is provided. */
  useEffect(() => {
    if (id) getAddressBook(id);
  }, [id]);

  /* ---------------------------------------------------------------------------
   * PROVIDER RETURN
   * ------------------------------------------------------------------------- */

  return (
    <AddressBookHandlerContext.Provider
      value={{
        service,
        action,
        data,
        loader,
        error,
        modalConfig,
        registry,
        autosave,
        label,
        ownerId: ownerId || accessTokenPayload.owner,
        searchInAddressBookParams,
        isBilling,
        fieldsToShow,
        callback,
        getAddressBook,
        saveAddressBook,
        saveRegistryAddressBook,
        setModalConfig,
      }}
    >
      {children}
    </AddressBookHandlerContext.Provider>
  );
};

export { AddressBookHandlerProvider };
